import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggingService } from '../services/logging.service';

/**
 * Global Angular {@link ErrorHandler} that funnels every unhandled error into
 * the AMIE logging pipeline as an operational `error.unhandled` event.
 *
 * @ticket EB-3798
 * Two defences shape this handler:
 * - Error-storm dedup: an Angular change-detection loop can throw the same
 *   error(s) thousands of times per second. Emitting each one would flood
 *   Graylog, so a message seen within {@link DEDUP_WINDOW_MS} is suppressed.
 *   The window is keyed per-message ({@link seen} map, not a single last-message
 *   slot) so a CD loop that alternates TWO distinct errors is still deduped —
 *   a single slot would never match either and emit every one.
 * - Defensive try/catch: a throw INSIDE `handleError` (e.g. the LoggingService
 *   itself failing) would re-enter the handler and loop, so the emit is wrapped
 *   and any failure swallowed.
 * Dev visibility is preserved: the error is always `console.error`d, matching
 * the default ErrorHandler behaviour developers rely on.
 *
 * `HttpErrorResponse` errors are deliberately NOT re-emitted here: every HTTP
 * failure is already captured by `httpErrorInterceptor` as `error.http` WITH
 * the request's original `correlation-id` (which an `HttpErrorResponse` cannot
 * surface — it exposes no request headers). Re-logging them as `error.unhandled`
 * would duplicate the event and lose that id, so `error.unhandled` is reserved
 * for genuine non-HTTP (runtime/JS) errors, which are not request-bound.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  /** Suppression window (ms) for repeated error messages. */
  private static readonly DEDUP_WINDOW_MS = 5000;

  /** Cap on the per-message dedup map so it can never grow unbounded. */
  private static readonly DEDUP_MAX = 20;

  private readonly loggingService = inject(LoggingService);

  /** Per-message last-emit timestamp (ms) for storm dedup. */
  private readonly seen = new Map<string, number>();

  handleError(error: unknown): void {
    console.error(error);

    // Already logged as `error.http` (with correlation-id) by the interceptor.
    if (error instanceof HttpErrorResponse) {
      return;
    }

    try {
      const fullMessage = this.extractFullMessage(error);
      if (this.isRecentlySeen(fullMessage, Date.now())) {
        return;
      }
      this.loggingService.error('error.unhandled', { full_message: fullMessage });
    } catch {
      // Swallow: a throw here would re-enter handleError and loop.
    }
  }

  /**
   * True when `message` was emitted within {@link DEDUP_WINDOW_MS}; otherwise
   * records it as seen. Evicts stale entries and caps the map size, so two (or
   * more) alternating errors are each deduped independently.
   */
  private isRecentlySeen(message: string, now: number): boolean {
    const last = this.seen.get(message);
    if (last !== undefined && now - last < GlobalErrorHandler.DEDUP_WINDOW_MS) {
      return true;
    }
    this.seen.set(message, now);
    if (this.seen.size > GlobalErrorHandler.DEDUP_MAX) {
      for (const [k, t] of this.seen) {
        if (now - t >= GlobalErrorHandler.DEDUP_WINDOW_MS) this.seen.delete(k);
      }
      const oldest = this.seen.keys().next().value;
      if (this.seen.size > GlobalErrorHandler.DEDUP_MAX && oldest !== undefined) {
        this.seen.delete(oldest);
      }
    }
    return false;
  }

  /** Prefer a JS Error stack; fall back to a string form of anything else. */
  private extractFullMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.stack ?? error.message;
    }
    return String(error);
  }
}
